import {
  LEGACY_MIGRATION_STATUS,
  legacyMigrationStatusSchema,
  statusEntries,
  type LegacyMigrationStatus,
} from '@flux/shared';
import type { JSX } from 'react';
import { z } from 'zod';
import { ListToolbar } from '@/components/data-table/list-toolbar.js';
import { MultiSelectFilter, PeriodFilter, type MultiSelectFilterOption } from './filter-controls.js';

/** Délai de la recherche (`pattern-lists-tables` §9). */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Statuts retenus quand l'URL n'en porte pas : « Ignoré » est exclu par
 * défaut (`06` §8.3), ce sont les réactivations d'abonnements non legacy.
 */
export const DEFAULT_MIGRATION_STATUSES: readonly LegacyMigrationStatus[] = ['migrated', 'simulated', 'failed'];

export interface MigrationFiltersValue {
  /**
   * Statuts demandés. `explicit` faux = défaut (rien dans l'URL) ; une liste
   * vide choisie explicitement (`status=[]`) = tous les statuts, « Ignoré » inclus.
   */
  status: LegacyMigrationStatus[];
  explicitStatus: boolean;
  search: string;
  /** ISO-8601 UTC, début du jour parisien choisi. */
  from?: string;
  /** ISO-8601 UTC, fin du jour parisien choisi. */
  to?: string;
}

export type MigrationFiltersPatch = Partial<{
  status: LegacyMigrationStatus[] | undefined;
  search: string | undefined;
  from: string | undefined;
  to: string | undefined;
}>;

/** Patch qui revient aux filtres par défaut (« Réinitialiser »). */
export const CLEAR_MIGRATION_FILTERS: MigrationFiltersPatch = {
  status: undefined,
  search: undefined,
  from: undefined,
  to: undefined,
};

const STATUS_OPTIONS: readonly MultiSelectFilterOption<LegacyMigrationStatus>[] = statusEntries(
  LEGACY_MIGRATION_STATUS,
).map((entry) => ({ value: entry.code, label: entry.label }));

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

const isoDateTime = z.iso.datetime();

/** Lit les filtres de l'onglet Migrations legacy depuis `useSearch({strict:false})`. */
export function readMigrationFilters(rawSearch: unknown): MigrationFiltersValue {
  const search = readRecord(rawSearch);
  const status = z.array(legacyMigrationStatusSchema).safeParse(search.status);
  const term = z.string().trim().min(1).safeParse(search.search);
  const from = isoDateTime.safeParse(search.from);
  const to = isoDateTime.safeParse(search.to);
  return {
    status: status.success ? status.data : [...DEFAULT_MIGRATION_STATUSES],
    explicitStatus: status.success,
    search: term.success ? term.data : '',
    ...(from.success ? { from: from.data } : {}),
    ...(to.success ? { to: to.data } : {}),
  };
}

function isDefaultStatus(value: MigrationFiltersValue): boolean {
  return (
    !value.explicitStatus ||
    (value.status.length === DEFAULT_MIGRATION_STATUSES.length &&
      DEFAULT_MIGRATION_STATUSES.every((code) => value.status.includes(code)))
  );
}

export function hasActiveMigrationFilters(value: MigrationFiltersValue): boolean {
  return !isDefaultStatus(value) || value.search !== '' || value.from !== undefined || value.to !== undefined;
}

export interface MigrationFiltersProps {
  value: MigrationFiltersValue;
  onChange: (patch: MigrationFiltersPatch) => void;
}

/**
 * Barre de filtres de l'onglet Migrations legacy (`06` §8.3) : recherche par
 * abonnement ou email, statut, période.
 */
export function MigrationFilters({ value, onChange }: MigrationFiltersProps): JSX.Element {
  return (
    <ListToolbar
      search={{
        value: value.search,
        onChange: (next) => {
          onChange({ search: next === '' ? undefined : next });
        },
        placeholder: 'Rechercher un abonnement ou un email…',
        label: 'Rechercher un abonnement ou un email',
        debounceMs: SEARCH_DEBOUNCE_MS,
      }}
      isFiltered={hasActiveMigrationFilters(value)}
      onReset={() => {
        onChange(CLEAR_MIGRATION_FILTERS);
      }}
    >
      <MultiSelectFilter
        label="Statut"
        ariaLabel="Filtrer par statut"
        options={STATUS_OPTIONS}
        value={value.status}
        onChange={(status) => {
          // Tout décocher = tous les statuts, « Ignoré » compris (`status=[]`), pas le retour au défaut.
          onChange({ status: status ?? [] });
        }}
      />
      <PeriodFilter
        from={value.from}
        to={value.to}
        fromLabel="Depuis le"
        toLabel="Jusqu’au"
        onChange={onChange}
      />
    </ListToolbar>
  );
}
